import {
  Button,
  ButtonVariant,
  DropdownMenu,
  Icon,
  IconName,
  Modal,
} from '@usealloy/component-library';
import { useContext, useState } from 'react';

import {
  PRODUCT_UPDATES_API_ENDPOINT,
  PRODUCT_UPDATES_API_HEADERS,
  PRODUCT_UPDATES_URL,
} from '~/constants';
import {
  ProductUpdates,
  useProductUpdatesData,
} from '~/dashboard/components/Sidebar/ProductUpdates';
import {
  ExternalLinkIcon,
  HelpMenuDescription,
  HelpMenuItemContent,
  HelpMenuTitle,
} from '~/dashboard/components/Sidebar/SidebarStyle';
import { UserContext } from '~/dashboard/context';
import { isIntercomAvailable, show as showIntercom } from '~/helpers/intercom';
import { openZendeskSupportForm } from '~/helpers/zendesk';

import { Sidebar } from '../component/Sidebar';
import { StyledFlyoutTriggerButton } from './FlyoutTriggerCaret';

const ProductUpdatesModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const productUpdatesData = useProductUpdatesData({
    endpoint: PRODUCT_UPDATES_API_ENDPOINT,
    headers: PRODUCT_UPDATES_API_HEADERS,
    limit: 10,
    enabled: isOpen,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Product Updates"
      size="medium"
      hasCloseIcon
      primaryAction={(
        <Button variant={ButtonVariant.primary} onClick={onClose}>
          Close
        </Button>
      )}
      tertiaryAction={(
        <Button
          variant={ButtonVariant.tertiary}
          onClick={() => {
            onClose();
            window.open(PRODUCT_UPDATES_URL, '_blank');
          }}
        >
          View Entire Update Collection
        </Button>
      )}
    >
      <ProductUpdates productUpdatesData={productUpdatesData} />
    </Modal>
  );
};

/**
 * "Resources" menu item — dropdown with Product Updates, Learn Alloy, and Support.
 *
 * Port of the legacy `SidebarMenuResourcesButton`
 * (`~/dashboard/components/Sidebar/Sidebar.jsx`) onto the Sidebar_v2 primitives.
 */
export const ResourcesMenuItem = () => {
  const { agent } = useContext(UserContext);
  const { isExpanded } = Sidebar.useSidebarCtx();
  const [isOpen, setIsOpen] = useState(false);
  const [showProductUpdates, setShowProductUpdates] = useState(false);

  return (
    <>
      <Sidebar.MenuItem>
        <DropdownMenu
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          modal={false}
          align="center"
          side="right"
          style={{ padding: '4px' }}
          trigger={(
            <StyledFlyoutTriggerButton
              data-dd-action-name="alloydd-sidebar-nav-resources"
              tooltip="Resources"
            >
              <Icon icon={IconName.Question} size="large" />
              <span className="fs-unmask">Resources</span>
              {isExpanded && (
                <Icon
                  icon={IconName.CaretRight}
                  style={{ flexShrink: 0, marginLeft: 'auto' }}
                />
              )}
            </StyledFlyoutTriggerButton>
          )}
        >
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-product-updates"
            onSelect={() => {
              setIsOpen(false);
              setShowProductUpdates(true);
            }}
          >
            <Icon icon={IconName.Gift} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Product Updates</HelpMenuTitle>
              <HelpMenuDescription>
                Get the latest product news
              </HelpMenuDescription>
            </HelpMenuItemContent>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-alloy-lab"
            onSelect={() => {
              setIsOpen(false);
              window.open('https://alloy.docebosaas.com/learn', '_blank');
            }}
          >
            <Icon icon={IconName.LifeRing} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Learn Alloy Better</HelpMenuTitle>
              <HelpMenuDescription>
                Our platform training portal
              </HelpMenuDescription>
            </HelpMenuItemContent>
            <ExternalLinkIcon icon={IconName.ArrowUpRightFromSquare} />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-support"
            onSelect={() => {
              setIsOpen(false);

              // The dashboard template renders exactly one support widget, chosen server
              // side by the intercom-messenger-chat flag, so whichever global exists is the
              // one to open. Checking availability rather than re-reading the flag means
              // this can't disagree with what the page actually loaded.
              if (isIntercomAvailable()) {
                showIntercom();
              } else {
                // UserContext's agent fields are optional; zendesk tolerates missing prefill data
                type ZendeskAgent = Parameters<typeof openZendeskSupportForm>[1];
                openZendeskSupportForm(isExpanded, agent as ZendeskAgent);
              }
            }}
          >
            <Icon icon={IconName.Message} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Support</HelpMenuTitle>
              <HelpMenuDescription>
                View our docs and get in touch
              </HelpMenuDescription>
            </HelpMenuItemContent>
          </DropdownMenu.Item>
        </DropdownMenu>
      </Sidebar.MenuItem>

      <ProductUpdatesModal
        isOpen={showProductUpdates}
        onClose={() => setShowProductUpdates(false)}
      />
    </>
  );
};
